import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import {
  Plus,
  X,
  Link2,
  ArrowRight,
  Database,
  Layers
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Advanced Query Builder with Joins
export const AdvancedQueryBuilder = ({
  advancedQuery,
  setAdvancedQuery,
  dataSources,
  operators,
  t
}) => {
  const [allFields, setAllFields] = useState({}); // Fields per collection
  const [joinOptions, setJoinOptions] = useState([]);

  // Fetch fields for a collection
  const fetchFieldsForCollection = async (collection) => {
    if (allFields[collection]) return allFields[collection];
    
    try {
      const response = await axios.get(`${API_URL}/api/custom-reports/fields/${collection}`);
      const fields = response.data.fields || [];
      setAllFields(prev => ({ ...prev, [collection]: fields }));
      return fields;
    } catch (error) {
      console.error('Error fetching fields:', error);
      return [];
    }
  };

  // Fetch join options for a collection
  const fetchJoinOptions = async (collection) => {
    try {
      const response = await axios.get(`${API_URL}/api/custom-reports/join-options/${collection}`);
      setJoinOptions(response.data.joins || []);
    } catch (error) {
      console.error('Error fetching join options:', error);
      setJoinOptions([]);
    }
  };

  // Handle primary source change
  const handlePrimarySourceChange = async (value) => {
    setAdvancedQuery(prev => ({
      ...prev,
      primary_source: value,
      joins: [],
      selected_fields: [],
      condition_groups: []
    }));
    await fetchFieldsForCollection(value);
    await fetchJoinOptions(value);
  };

  // Add a join
  const addJoin = async (joinOption) => {
    const newJoin = {
      id: `join_${Date.now()}`,
      from_collection: advancedQuery.primary_source,
      to_collection: joinOption.target,
      from_field: joinOption.from_field,
      to_field: joinOption.to_field,
      join_type: 'left',
      alias: joinOption.target
    };
    
    setAdvancedQuery(prev => ({
      ...prev,
      joins: [...prev.joins, newJoin]
    }));
    
    // Fetch fields for the joined collection
    await fetchFieldsForCollection(joinOption.target);
    
    // Refresh join options to show more possibilities
    await fetchJoinOptions(joinOption.target);
  };

  // Remove a join
  const removeJoin = (joinId) => {
    setAdvancedQuery(prev => ({
      ...prev,
      joins: prev.joins.filter(j => j.id !== joinId),
      // Remove fields from the removed collection
      selected_fields: prev.selected_fields.filter(f => {
        const join = prev.joins.find(j => j.id === joinId);
        if (!join) return true;
        return !f.startsWith(`${join.alias}.`) && !f.startsWith(`${join.to_collection}.`);
      })
    }));
  };

  // Toggle field selection
  const toggleFieldSelection = (collection, fieldName) => {
    const fullFieldName = `${collection}.${fieldName}`;
    setAdvancedQuery(prev => {
      const current = prev.selected_fields || [];
      if (current.includes(fullFieldName)) {
        return { ...prev, selected_fields: current.filter(f => f !== fullFieldName) };
      } else {
        return { ...prev, selected_fields: [...current, fullFieldName] };
      }
    });
  };

  // Add condition group
  const addConditionGroup = () => {
    setAdvancedQuery(prev => ({
      ...prev,
      condition_groups: [
        ...prev.condition_groups,
        {
          id: `group_${Date.now()}`,
          logic: 'and',
          conditions: [{
            id: `cond_${Date.now()}`,
            field: '',
            operator: 'equals',
            value: ''
          }],
          groups: []
        }
      ]
    }));
  };

  // Add condition to group
  const addConditionToGroup = (groupIndex) => {
    const newGroups = [...advancedQuery.condition_groups];
    newGroups[groupIndex].conditions.push({
      id: `cond_${Date.now()}`,
      field: '',
      operator: 'equals',
      value: ''
    });
    setAdvancedQuery(prev => ({ ...prev, condition_groups: newGroups }));
  };

  // Update condition
  const updateCondition = (groupIndex, condIndex, field, value) => {
    const newGroups = [...advancedQuery.condition_groups];
    newGroups[groupIndex].conditions[condIndex][field] = value;
    setAdvancedQuery(prev => ({ ...prev, condition_groups: newGroups }));
  };

  // Remove condition
  const removeCondition = (groupIndex, condIndex) => {
    const newGroups = [...advancedQuery.condition_groups];
    newGroups[groupIndex].conditions.splice(condIndex, 1);
    if (newGroups[groupIndex].conditions.length === 0) {
      newGroups.splice(groupIndex, 1);
    }
    setAdvancedQuery(prev => ({ ...prev, condition_groups: newGroups }));
  };

  // Update group logic
  const updateGroupLogic = (groupIndex, logic) => {
    const newGroups = [...advancedQuery.condition_groups];
    newGroups[groupIndex].logic = logic;
    setAdvancedQuery(prev => ({ ...prev, condition_groups: newGroups }));
  };

  // Get all available fields (from primary + joined collections)
  const getAllAvailableFields = () => {
    const fields = [];
    
    // Primary source fields
    if (advancedQuery.primary_source && allFields[advancedQuery.primary_source]) {
      allFields[advancedQuery.primary_source].forEach(f => {
        fields.push({
          ...f,
          fullName: `${advancedQuery.primary_source}.${f.name}`,
          collection: advancedQuery.primary_source,
          displayLabel: `[${advancedQuery.primary_source}] ${f.label}`
        });
      });
    }
    
    // Joined collections fields
    advancedQuery.joins.forEach(join => {
      const alias = join.alias || join.to_collection;
      if (allFields[join.to_collection]) {
        allFields[join.to_collection].forEach(f => {
          fields.push({
            ...f,
            fullName: `${alias}.${f.name}`,
            collection: alias,
            displayLabel: `[${alias}] ${f.label}`
          });
        });
      }
    });
    
    return fields;
  };

  // Get join options excluding already joined
  const getAvailableJoinOptions = () => {
    const joinedCollections = advancedQuery.joins.map(j => j.to_collection);
    return joinOptions.filter(opt => !joinedCollections.includes(opt.target));
  };

  const availableFields = getAllAvailableFields();
  const availableJoins = getAvailableJoinOptions();

  return (
    <div className="space-y-6" data-testid="advanced-query-builder">
      {/* Step 1: Primary Data Source */}
      <div className="space-y-2">
        <Label className="text-sm font-medium flex items-center gap-2">
          <Database className="h-4 w-4" />
          Step 1: Primary Data Source
        </Label>
        <Select 
          value={advancedQuery.primary_source || ''} 
          onValueChange={handlePrimarySourceChange}
        >
          <SelectTrigger data-testid="primary-source-select">
            <SelectValue placeholder="Select primary collection" />
          </SelectTrigger>
          <SelectContent>
            {dataSources.map(ds => (
              <SelectItem key={ds.value} value={ds.value}>
                {t(`custom.dataSources.${ds.value}`) || ds.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Step 2: Add Joins */}
      {advancedQuery.primary_source && (
        <div className="space-y-3">
          <Label className="text-sm font-medium flex items-center gap-2">
            <Link2 className="h-4 w-4" />
            Step 2: Join Collections
          </Label>
          
          {/* Current Joins */}
          {advancedQuery.joins.length > 0 && (
            <div className="space-y-2">
              {advancedQuery.joins.map((join, idx) => (
                <div 
                  key={join.id} 
                  className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg border border-blue-200"
                >
                  <Badge variant="outline">{advancedQuery.primary_source}</Badge>
                  <ArrowRight className="h-4 w-4 text-blue-500" />
                  <Badge className="bg-blue-500">{join.alias || join.to_collection}</Badge>
                  <span className="text-xs text-gray-500">
                    ({join.from_field} = {join.to_field})
                  </span>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-6 w-6 ml-auto text-red-500"
                    onClick={() => removeJoin(join.id)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Available Joins */}
          {availableJoins.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {availableJoins.map(joinOpt => (
                <Button
                  key={joinOpt.target}
                  variant="outline"
                  size="sm"
                  onClick={() => addJoin(joinOpt)}
                  className="text-xs"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Join {joinOpt.label}
                </Button>
              ))}
            </div>
          )}

          {availableJoins.length === 0 && advancedQuery.joins.length === 0 && (
            <p className="text-sm text-gray-500">No join options available for this collection.</p>
          )}
        </div>
      )}

      {/* Step 3: Select Fields */}
      {advancedQuery.primary_source && (
        <div className="space-y-2">
          <Label className="text-sm font-medium flex items-center gap-2">
            <Layers className="h-4 w-4" />
            Step 3: Select Fields to Display
          </Label>
          <div className="border rounded-lg p-3 max-h-64 overflow-y-auto">
            {/* Group fields by collection */}
            {[advancedQuery.primary_source, ...advancedQuery.joins.map(j => j.alias || j.to_collection)].map(collection => {
              const sourceCollection = advancedQuery.joins.find(j => (j.alias || j.to_collection) === collection)?.to_collection || collection;
              const fields = allFields[sourceCollection] || [];
              
              return (
                <div key={collection} className="mb-3">
                  <div className="font-medium text-sm text-gray-600 mb-1 bg-gray-100 px-2 py-1 rounded">
                    {collection === advancedQuery.primary_source ? (
                      <span className="flex items-center gap-1">
                        <Database className="h-3 w-3" />
                        {collection} (Primary)
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <Link2 className="h-3 w-3" />
                        {collection} (Joined)
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-1 pl-2">
                    {fields.map(field => {
                      const fullFieldName = `${collection}.${field.name}`;
                      const isSelected = advancedQuery.selected_fields.includes(fullFieldName);
                      return (
                        <label 
                          key={fullFieldName}
                          className="flex items-center gap-2 text-xs cursor-pointer hover:bg-gray-50 p-1 rounded"
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleFieldSelection(collection, field.name)}
                          />
                          <span className={field.is_custom ? 'text-blue-600' : ''}>
                            {field.label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-gray-500">
            Selected: {advancedQuery.selected_fields.length} field(s)
            {advancedQuery.selected_fields.length === 0 && ' (All fields will be shown)'}
          </p>
        </div>
      )}

      {/* Step 4: Conditions */}
      {advancedQuery.primary_source && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Step 4: Filter Conditions</Label>
            <Button variant="outline" size="sm" onClick={addConditionGroup}>
              <Plus className="h-4 w-4 mr-1" />
              Add Condition Group
            </Button>
          </div>

          {advancedQuery.condition_groups?.map((group, groupIndex) => (
            <Card key={group.id} className="border-l-4 border-l-purple-500">
              <CardContent className="p-4 space-y-3">
                {/* Group Logic */}
                {group.conditions.length > 1 && (
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-gray-500">Combine with:</span>
                    <Select 
                      value={group.logic} 
                      onValueChange={(v) => updateGroupLogic(groupIndex, v)}
                    >
                      <SelectTrigger className="w-24 h-7 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="and">{t('custom.and')}</SelectItem>
                        <SelectItem value="or">{t('custom.or')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Conditions in Group */}
                {group.conditions.map((condition, condIndex) => (
                  <div key={condition.id} className="flex items-center gap-2 flex-wrap">
                    {condIndex > 0 && (
                      <Badge variant="outline" className="text-xs">
                        {group.logic === 'and' ? t('custom.and') : t('custom.or')}
                      </Badge>
                    )}
                    
                    {/* Field selector with collection prefix */}
                    <Select 
                      value={condition.field} 
                      onValueChange={(v) => updateCondition(groupIndex, condIndex, 'field', v)}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Select field" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableFields.map(f => (
                          <SelectItem key={f.fullName} value={f.fullName}>
                            {f.displayLabel}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {/* Operator */}
                    <Select 
                      value={condition.operator} 
                      onValueChange={(v) => updateCondition(groupIndex, condIndex, 'operator', v)}
                    >
                      <SelectTrigger className="w-36">
                        <SelectValue placeholder="Operator" />
                      </SelectTrigger>
                      <SelectContent>
                        {operators.map(op => (
                          <SelectItem key={op.value} value={op.value}>
                            {t(`custom.operators.${op.value}`) || op.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {/* Value input */}
                    {!['is_empty', 'is_not_empty'].includes(condition.operator) && (
                      <Input
                        className="w-40"
                        placeholder={condition.operator === 'days_ago' ? 'Days (e.g., 30)' : 'Value'}
                        value={condition.value || ''}
                        onChange={(e) => updateCondition(groupIndex, condIndex, 'value', e.target.value)}
                      />
                    )}

                    {condition.operator === 'between' && (
                      <Input
                        className="w-40"
                        placeholder="Second value"
                        value={condition.value2 || ''}
                        onChange={(e) => updateCondition(groupIndex, condIndex, 'value2', e.target.value)}
                      />
                    )}

                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="h-8 w-8 text-red-500"
                      onClick={() => removeCondition(groupIndex, condIndex)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}

                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-purple-600"
                  onClick={() => addConditionToGroup(groupIndex)}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Condition
                </Button>
              </CardContent>
            </Card>
          ))}

          {advancedQuery.condition_groups?.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded-lg">
              No conditions added. Click "Add Condition Group" to filter results.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default AdvancedQueryBuilder;
